import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSSE } from '../hooks/useSSE';
import { useToast } from '../hooks/useToast';
import { useRepositoryStore } from '../stores/useRepositoryStore';
import { messages } from '../constants/messages';
import AuthScreen from '../components/AuthScreen';
import Sidebar from '../components/Sidebar';
import ToastContainer from '../components/ToastContainer';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export default function Dashboard() {
  const { authStatus, refreshOrganizations } = useAuth();
  const { showToast } = useToast();
  const loadRepositories = useRepositoryStore(state => state.loadRepositories);
  const loadActivityHistory = useRepositoryStore(state => state.loadActivityHistory);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Handle URL parameters for redirects
    if (
      searchParams.get('subscription') === 'success' ||
      searchParams.get('subscription') === 'canceled'
    ) {
      navigate('/settings', { replace: true });
    } else if (
      searchParams.get('success') === 'slack_connected' ||
      searchParams.get('error') === 'slack_auth_failed'
    ) {
      navigate('/integrations', { replace: true });
    }
  }, [searchParams, navigate]);

  // Scroll to top when route changes and track pageview
  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Track pageview in Google Analytics
    const pageName = location.pathname === '/' ? 'repos' : location.pathname.substring(1);
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_location: `${window.location.origin}${location.pathname}${location.search}`,
        page_title: pageName,
      });
    }
  }, [location]);

  // Single SSE handler for all events at Dashboard level (persists across page navigation)
  useSSE((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      // Handle installation created event
      if (
        data.type === 'github_webhook' &&
        data.event === 'installation' &&
        data.action === 'created'
      ) {
        console.log('New GitHub App installation detected');
        refreshOrganizations();
        showToast('GitHub App installed successfully!', 'success');
      }

      // Handle installation deleted event
      if (
        data.type === 'github_webhook' &&
        data.event === 'installation' &&
        data.action === 'deleted'
      ) {
        console.log('GitHub App installation removed');
        refreshOrganizations();
        showToast('GitHub App uninstalled', 'info');
      }

      // Handle freeze/unfreeze events and reload repository data
      if (
        data.type === messages.events.repositoryFrozen ||
        data.type === messages.events.repositoryUnfrozen ||
        data.type === 'schedule_created' ||
        data.type === 'schedule_updated' ||
        data.type === 'schedule_deleted'
      ) {
        console.log(`Repository event received: ${data.type}`);
        loadRepositories();
        loadActivityHistory();
      }

      // Handle other GitHub webhook events that affect repositories
      if (data.type === 'github_webhook') {
        if (
          data.event === 'pull_request' ||
          data.event === 'installation_repositories' ||
          data.event === 'push'
        ) {
          console.log(`GitHub ${data.event} event received`);
          loadRepositories();
          loadActivityHistory();
        }
      }
    } catch (error) {
      console.error('Error processing SSE message:', error);
    }
  });

  if (!authStatus.authenticated) {
    return (
      <>
        <AuthScreen />
        <ToastContainer />
      </>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-content">
        <Outlet />
      </main>
      <ToastContainer />
    </div>
  );
}
